import {useAppOverrides} from 'contexts/AppOverridesContext';
import {getAppearanceSettingsRef} from 'contexts/ColorSchemeContext';
import {setDoc} from 'firebase/firestore';
import {useAuthUid} from 'hooks/user/useUserId';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {z} from 'zod';

/**
 * The course sidebar variants a user can choose between. `classic` is the
 * sidebar every course has always had; `flat` is the lighter, borderless
 * variant being trialled. The styles themselves live in
 * `course/components/sidebar/menuItemStyles`.
 */
export const SIDEBAR_STYLES = ['classic', 'flat'] as const;

/** Zod schema for {@link SidebarStyle}. */
export const SidebarStyleSchema = z.enum(SIDEBAR_STYLES);

/** A user's stored sidebar choice. */
export type SidebarStyle = z.infer<typeof SidebarStyleSchema>;

/**
 * How the course sidebar arranges its entries. `submenus` nests Home, Code,
 * and Forums under collapsible submenus; `grouped` shows only direct links, in
 * a "Teaching" section for mentors and above and a "Student" section (see
 * `course/components/sidebar/GroupedSidebarMenu`).
 */
export const SIDEBAR_LAYOUTS = ['submenus', 'grouped'] as const;

/** Zod schema for {@link SidebarLayout}. */
export const SidebarLayoutSchema = z.enum(SIDEBAR_LAYOUTS);

/** A user's stored sidebar layout choice. */
export type SidebarLayout = z.infer<typeof SidebarLayoutSchema>;

/**
 * The main-forum layouts a user can choose between. `classic` is the two-pane
 * forum at `/:courseId/forum`; `feed` is the Reddit-style experiment at
 * `/:courseId/forum2` (see `course/forum/pages/feed`). Both read and write
 * the same forum data.
 */
export const FORUM_STYLES = ['classic', 'feed'] as const;

/** Zod schema for {@link ForumStyle}. */
export const ForumStyleSchema = z.enum(FORUM_STYLES);

/** A user's stored main-forum layout choice. */
export type ForumStyle = z.infer<typeof ForumStyleSchema>;

/**
 * The route segment under `/:courseId` that serves each {@link ForumStyle}.
 * Must match the forum routes in `CourseRoutes`.
 */
export const FORUM_STYLE_SUBPAGE: Record<ForumStyle, string> = {
  classic: 'forum',
  feed: 'forum2',
};

/**
 * The slice of the per-user appearance settings document this context owns.
 * The same document also holds the color scheme (see `ColorSchemeContext`);
 * each context parses only its own fields, leniently, so an unknown value
 * falls back to the default instead of failing the whole document.
 */
const UiPreferencesSchema = z.object({
  sidebarStyle: SidebarStyleSchema.optional(),
  sidebarLayout: SidebarLayoutSchema.optional(),
  forumStyle: ForumStyleSchema.optional(),
});

/**
 * Picks the value in effect for one preference. While the deploy offers the
 * choice, the user's pending or stored choice wins over the deploy default;
 * once the choice is withdrawn, a stored choice is ignored so every user gets
 * the default.
 *
 * @param isChoiceAvailable - Whether the deploy offers this choice.
 * @param pending - The user's last unacknowledged write, or `null`.
 * @param stored - The user's stored choice. When omitted, the user has none.
 * @param fallback - The deploy default.
 * @return The value in effect.
 * @throws Never.
 */
function resolvePreference<T>({
  isChoiceAvailable,
  pending,
  stored,
  fallback,
}: {
  isChoiceAvailable: boolean;
  pending: T | null;
  stored?: T;
  fallback: T;
}): T {
  if (!isChoiceAvailable) {
    return fallback;
  }
  return pending ?? stored ?? fallback;
}

/** Value exposed by {@link UiPreferencesProvider}. */
export interface UiPreferencesContextData {
  /**
   * The sidebar style in effect: the stored choice while the choice is
   * offered, else the deploy default.
   */
  sidebarStyle: SidebarStyle;
  /**
   * Whether the deploy offers the sidebar choice
   * (`AppOverrides.hasSidebarStyleSetting`). When false the settings control
   * should be hidden and the deploy default applies to everyone.
   */
  isSidebarStyleChoiceAvailable: boolean;
  /**
   * Stores a new sidebar choice for the signed-in user. A no-op (logged at
   * debug level) when no user is signed in.
   */
  setSidebarStyle: (style: SidebarStyle) => void;
  /**
   * The sidebar layout in effect: the stored choice while the choice is
   * offered, else the deploy default.
   */
  sidebarLayout: SidebarLayout;
  /**
   * Whether the deploy offers the sidebar layout choice
   * (`AppOverrides.hasSidebarLayoutSetting`). When false the settings control
   * should be hidden and the deploy default applies to everyone.
   */
  isSidebarLayoutChoiceAvailable: boolean;
  /**
   * Stores a new sidebar layout choice for the signed-in user. A no-op
   * (logged at debug level) when no user is signed in.
   */
  setSidebarLayout: (layout: SidebarLayout) => void;
  /**
   * The forum layout in effect: the stored choice while the choice is
   * offered, else the deploy default.
   */
  forumStyle: ForumStyle;
  /**
   * Whether the deploy offers the forum choice
   * (`AppOverrides.hasForumStyleSetting`). When false the settings control
   * should be hidden and the deploy default applies to everyone.
   */
  isForumStyleChoiceAvailable: boolean;
  /**
   * Stores a new forum choice for the signed-in user. A no-op (logged at
   * debug level) when no user is signed in.
   */
  setForumStyle: (style: ForumStyle) => void;
}

const UiPreferencesContext = createContext<UiPreferencesContextData>({
  sidebarStyle: 'classic',
  isSidebarStyleChoiceAvailable: false,
  setSidebarStyle: () => {},
  sidebarLayout: 'submenus',
  isSidebarLayoutChoiceAvailable: false,
  setSidebarLayout: () => {},
  forumStyle: 'classic',
  isForumStyleChoiceAvailable: false,
  setForumStyle: () => {},
});

/**
 * Resolves the signed-in user's experimental UI choices: the course sidebar
 * style and layout, and the main-forum layout.
 *
 * The choices are read live from the user's appearance settings document (the
 * one `ColorSchemeProvider` reads too; the Firestore SDK shares a single
 * listener between them), so they follow the user across devices. Until that
 * document loads, and for any choice the deploy does not offer, the deploy
 * defaults (`AppOverrides.defaultSidebarStyle`,
 * `AppOverrides.defaultSidebarLayout`, `AppOverrides.defaultForumStyle`)
 * apply. Consumers: `CourseSidebar`, the
 * forum entries in the sidebar submenus, and the classic forum route's
 * redirect (`course/forum/pages/feed/ClassicForumRedirect`).
 */
export function UiPreferencesProvider({children}: {children: ReactNode}) {
  const {
    hasSidebarStyleSetting,
    defaultSidebarStyle,
    hasSidebarLayoutSetting,
    defaultSidebarLayout,
    hasForumStyleSetting,
    defaultForumStyle,
  } = useAppOverrides();
  const uid = useAuthUid();
  const settingsRef = useMemo(() => getAppearanceSettingsRef(uid), [uid]);
  const [settingsData] = useDocumentData(settingsRef);
  // Local echo of the last writes so the UI switches immediately rather than
  // waiting for the Firestore listener to deliver the stored values.
  const [pendingSidebarStyle, setPendingSidebarStyle] =
    useState<SidebarStyle | null>(null);
  const [pendingSidebarLayout, setPendingSidebarLayout] =
    useState<SidebarLayout | null>(null);
  const [pendingForumStyle, setPendingForumStyle] = useState<ForumStyle | null>(
    null,
  );

  const stored = useMemo(() => {
    const parsed = UiPreferencesSchema.safeParse(settingsData ?? {});
    return parsed.success ? parsed.data : {};
  }, [settingsData]);

  const sidebarStyle = resolvePreference<SidebarStyle>({
    isChoiceAvailable: Boolean(hasSidebarStyleSetting),
    pending: pendingSidebarStyle,
    stored: stored.sidebarStyle,
    fallback: defaultSidebarStyle ?? 'classic',
  });
  const sidebarLayout = resolvePreference<SidebarLayout>({
    isChoiceAvailable: Boolean(hasSidebarLayoutSetting),
    pending: pendingSidebarLayout,
    stored: stored.sidebarLayout,
    fallback: defaultSidebarLayout ?? 'submenus',
  });
  const forumStyle = resolvePreference<ForumStyle>({
    isChoiceAvailable: Boolean(hasForumStyleSetting),
    pending: pendingForumStyle,
    stored: stored.forumStyle,
    fallback: defaultForumStyle ?? 'classic',
  });

  const persist = useCallback(
    (field: keyof z.infer<typeof UiPreferencesSchema>, value: string) => {
      if (!settingsRef) {
        console.debug(`[UiPreferences] no signed-in user; ${field} not stored`);
        return;
      }
      setDoc(settingsRef, {[field]: value}, {merge: true}).catch(e => {
        console.debug(`[UiPreferences] failed to persist ${field}`, value, e);
      });
    },
    [settingsRef],
  );

  const setSidebarStyle = useCallback(
    (next: SidebarStyle) => {
      setPendingSidebarStyle(next);
      persist('sidebarStyle', next);
    },
    [persist],
  );

  const setSidebarLayout = useCallback(
    (next: SidebarLayout) => {
      setPendingSidebarLayout(next);
      persist('sidebarLayout', next);
    },
    [persist],
  );

  const setForumStyle = useCallback(
    (next: ForumStyle) => {
      setPendingForumStyle(next);
      persist('forumStyle', next);
    },
    [persist],
  );

  const value = useMemo<UiPreferencesContextData>(
    () => ({
      sidebarStyle,
      isSidebarStyleChoiceAvailable: Boolean(hasSidebarStyleSetting),
      setSidebarStyle,
      sidebarLayout,
      isSidebarLayoutChoiceAvailable: Boolean(hasSidebarLayoutSetting),
      setSidebarLayout,
      forumStyle,
      isForumStyleChoiceAvailable: Boolean(hasForumStyleSetting),
      setForumStyle,
    }),
    [
      sidebarStyle,
      hasSidebarStyleSetting,
      setSidebarStyle,
      sidebarLayout,
      hasSidebarLayoutSetting,
      setSidebarLayout,
      forumStyle,
      hasForumStyleSetting,
      setForumStyle,
    ],
  );

  return (
    <UiPreferencesContext.Provider value={value}>
      {children}
    </UiPreferencesContext.Provider>
  );
}

/**
 * Returns the active UI preferences.
 *
 * @return The nearest {@link UiPreferencesContextData}. Without a provider,
 *     returns the classic sidebar and forum with no choices available and
 *     no-op setters.
 * @throws Never.
 */
export function useUiPreferences(): UiPreferencesContextData {
  return useContext(UiPreferencesContext);
}
